import React from "react";
import { Col, Row } from "antd";

import styles from "./styles.module.css";

/**
 * The permission grid on the role create and edit screens.
 *
 * It used to be an antd `Card` per permission with a `Switch` wedged into the
 * title bar. Three things were wrong with that: the borderless card carries
 * antd's own drop shadow, so a form full of them read as a pile of floating
 * tiles rather than a set of choices; only the switch itself was clickable, a
 * ~40px target beside a card the user is already pointing at; and nothing about
 * a card that is off looked any different from one that is on except the switch
 * at the far end of the title.
 *
 * Now the whole card is the control. It is flat, it states its selected state
 * with its border and ground rather than with one small widget, and it is a
 * real `role="switch"` so a keyboard reaches it and a screen reader announces
 * what it is.
 *
 * Both screens render this same component: they were a copy of each other, and
 * the copy is how they drifted apart in the first place.
 */
const PermissionPicker = ({ permissions = [], value = [], onChange }) => {
  const toggle = (id) =>
    onChange(
      value.includes(id)
        ? value.filter((current) => current !== id)
        : [...value, id],
    );

  return (
    <Row gutter={[16, 16]}>
      {permissions.map((permission) => {
        const checked = value.includes(permission.id);

        return (
          <Col xs={24} lg={8} key={permission.id}>
            <div
              role="switch"
              tabIndex={0}
              aria-checked={checked}
              aria-label={permission.display_name}
              className={`${styles.card} ${checked ? styles.cardOn : ""}`}
              onClick={() => toggle(permission.id)}
              onKeyDown={(event) => {
                /* Space and Enter are what a switch answers to. Space scrolls
                   the page by default, so it has to be swallowed here. */
                if (event.key === " " || event.key === "Enter") {
                  event.preventDefault();
                  toggle(permission.id);
                }
              }}
            >
              <div className={styles.head}>
                <span className={styles.title}>{permission.display_name}</span>
                {/* Drawn rather than an antd <Switch>: that renders a <button>,
                    and a button inside a clickable control is both invalid
                    markup and a second thing for the click to land on. */}
                <span className={styles.track} aria-hidden="true">
                  <span className={styles.knob} />
                </span>
              </div>
              {permission?.description && (
                <p className={styles.description}>{permission.description}</p>
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
};

export default PermissionPicker;
